import type { ID, ResourceResponse, ThunkReturnType } from "types";
import { createAsyncThunk } from "infrastructure/store/lib";
import type { RootState, AsyncThunk } from "infrastructure/store";
import { ApiState } from "infrastructure/api";
import { ApiRouteTypes } from "consts/enpoints/api";
import { MenuItem } from "consts/openBanking";
import { concat } from "util/support";
import { payloadCreator } from "util/api";
import type {
    BankingSdkConnector,
    BankingSdkConnectorsRecipientsPivot,
    BankingSdkConnectorsRecipientsPivotResponse,
    BankingSdkConnectorsResponse,
    BankingSdkRecipient,
    BankingSdkRecipientsResponse,
    IbanXsBank,
    IbanXsBanksCountriesPivot,
    IbanXsBanksCountriesPivotResponse,
    IbanXsBanksRecipientsPivot,
    IbanXsBanksRecipientsPivotResponse,
    IbanXsBanksResponse
} from "./types";
import { sliceToken } from "./state";
import {
    getBankingSdkConnectors,
    updateBankingSdkConnector,
    getIbanXsBanks,
    updateIbanXsBanks,
    getBankingSdkRecipients,
    createBankingSdkRecipient,
    updateBankingSdkRecipient,
    deleteBankingSdkRecipient,
    getBankingSdkConnectorsRecipientsPivot,
    updateBankingSdkConnectorsRecipientsPivot,
    deleteBankingSdkConnectorsRecipientsPivot,
    getIbanXsBanksRecipientsPivot,
    updateIbanXsBanksRecipientsPivot,
    deleteIbanXsBanksRecipientsPivot,
    getIbanXsBanksCountriesPivot,
    updateIbanXsBanksCountriesPivot,
    deleteIbanXsBanksCountriesPivot,
    createBankingSdkConnectorsRecipientsPivot,
    createIbanXsBanksRecipientsPivot,
    createIbanXsBanksCountriesPivot
} from "./api";

export const getBankingSdkConnectorsThunk: AsyncThunk<
    ThunkReturnType<BankingSdkConnectorsResponse>,
    string | undefined,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.GetBankingSdkConnectors]),
    payloadCreator(getBankingSdkConnectors),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.BankingSDK].tableLoadingState);
        }
    }
);

export const updateBankingSdkConnectorThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<BankingSdkConnector>>,
    Partial<BankingSdkConnector>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.PutBankingSdkConnectors]),
    payloadCreator(updateBankingSdkConnector),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.BankingSDK].tableLoadingState);
        }
    }
);

export const getIbanXsBanksThunk: AsyncThunk<
    ThunkReturnType<IbanXsBanksResponse>,
    string | undefined,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.GetIbanXsBanks]),
    payloadCreator(getIbanXsBanks),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.IbanXS].tableLoadingState);
        }
    }
);

export const updateIbanXsBankThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<IbanXsBank>>,
    Partial<IbanXsBank>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.PutIbanXsBanks]),
    payloadCreator(updateIbanXsBanks),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.IbanXS].tableLoadingState);
        }
    }
);

export const getBankingSdkRecipientsThunk: AsyncThunk<
    ThunkReturnType<BankingSdkRecipientsResponse>,
    string | undefined,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.GetBankingSdkRecipients]),
    payloadCreator(getBankingSdkRecipients),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.BankingSDKRecipients].tableLoadingState);
        }
    }
);

export const createBankingSdkRecipientThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<BankingSdkRecipient>>,
    Partial<BankingSdkRecipient>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.PostBankingSdkRecipients]),
    payloadCreator(createBankingSdkRecipient),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.BankingSDKRecipients].tableLoadingState);
        }
    }
);

export const updateBankingSdkRecipientThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<BankingSdkRecipient>>,
    Partial<BankingSdkRecipient>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.PutBankingSdkRecipients]),
    payloadCreator(updateBankingSdkRecipient),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.BankingSDKRecipients].tableLoadingState);
        }
    }
);

export const deleteBankingSdkRecipientThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<ID>>,
    Pick<BankingSdkRecipient, 'coreId'>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.DeleteBankingSdkRecipients]),
    payloadCreator(deleteBankingSdkRecipient),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.BankingSDKRecipients].tableLoadingState);
        }
    }
);

export const getBankingSdkConnectorsRecipientsPivotThunk: AsyncThunk<
    ThunkReturnType<BankingSdkConnectorsRecipientsPivotResponse>,
    string | undefined,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.GetBankingSdkConnectorsRecipientsPivot]),
    payloadCreator(getBankingSdkConnectorsRecipientsPivot),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.BankingSDKConnectorsRecipientsPivot].tableLoadingState);
        }
    }
);

export const createBankingSdkConnectorsRecipientsPivotThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<BankingSdkConnectorsRecipientsPivot>>,
    Partial<BankingSdkConnectorsRecipientsPivot>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.PostBankingSdkConnectorsRecipientsPivot]),
    payloadCreator(createBankingSdkConnectorsRecipientsPivot),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.BankingSDKConnectorsRecipientsPivot].tableLoadingState);
        }
    }
);

export const updateBankingSdkConnectorsRecipientsPivotThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<BankingSdkConnectorsRecipientsPivot>>,
    Partial<BankingSdkConnectorsRecipientsPivot>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.PutBankingSdkConnectorsRecipientsPivot]),
    payloadCreator(updateBankingSdkConnectorsRecipientsPivot),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.BankingSDKConnectorsRecipientsPivot].tableLoadingState);
        }
    }
);

export const deleteBankingSdkConnectorsRecipientsPivotThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<ID>>,
    Pick<BankingSdkConnectorsRecipientsPivot, 'coreId'>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.DeleteBankingSdkConnectorsRecipientsPivot]),
    payloadCreator(deleteBankingSdkConnectorsRecipientsPivot),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.BankingSDKConnectorsRecipientsPivot].tableLoadingState);
        }
    }
);

export const getIbanXsBanksRecipientsPivotThunk: AsyncThunk<
    ThunkReturnType<IbanXsBanksRecipientsPivotResponse>,
    string | undefined,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.GetIbanXsBanksRecipientsPivot]),
    payloadCreator(getIbanXsBanksRecipientsPivot),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.IbanXSBanksRecipientsPivot].tableLoadingState);
        }
    }
);

export const createIbanXsBanksRecipientsPivotThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<IbanXsBanksRecipientsPivot>>,
    Partial<IbanXsBanksRecipientsPivot>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.PostIbanXsBanksRecipientsPivot]),
    payloadCreator(createIbanXsBanksRecipientsPivot),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.IbanXSBanksRecipientsPivot].tableLoadingState);
        }
    }
);

export const updateIbanXsBanksRecipientsPivotThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<IbanXsBanksRecipientsPivot>>,
    Partial<IbanXsBanksRecipientsPivot>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.PutIbanXsBanksRecipientsPivot]),
    payloadCreator(updateIbanXsBanksRecipientsPivot),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.IbanXSBanksRecipientsPivot].tableLoadingState);
        }
    }
);

export const deleteIbanXsBanksRecipientsPivotThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<ID>>,
    Pick<IbanXsBanksRecipientsPivot, 'coreId'>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.DeleteIbanXsBanksRecipientsPivot]),
    payloadCreator(deleteIbanXsBanksRecipientsPivot),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.IbanXSBanksRecipientsPivot].tableLoadingState);
        }
    }
);

export const getIbanXsBanksCountriesPivotThunk: AsyncThunk<
    ThunkReturnType<IbanXsBanksCountriesPivotResponse>,
    string | undefined,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.GetIbanXsBanksCountriesPivot]),
    payloadCreator(getIbanXsBanksCountriesPivot),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.IbanXSBanksCountriesPivot].tableLoadingState);
        }
    }
);

export const createIbanXsBanksCountriesPivotThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<IbanXsBanksCountriesPivot>>,
    Partial<IbanXsBanksCountriesPivot>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.PostIbanXsBanksCountriesPivot]),
    payloadCreator(createIbanXsBanksCountriesPivot),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.IbanXSBanksCountriesPivot].tableLoadingState);
        }
    }
);

export const updateIbanXsBanksCountriesPivotThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<IbanXsBanksCountriesPivot>>,
    Partial<IbanXsBanksCountriesPivot>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.PutIbanXsBanksCountriesPivot]),
    payloadCreator(updateIbanXsBanksCountriesPivot),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.IbanXSBanksCountriesPivot].tableLoadingState);
        }
    }
);

export const deleteIbanXsBanksCountriesPivotThunk: AsyncThunk<
    ThunkReturnType<ResourceResponse<ID>>,
    Pick<IbanXsBanksCountriesPivot, 'coreId'>,
    {}
> = createAsyncThunk(
    concat([sliceToken, ApiRouteTypes.DeleteIbanXsBanksCountriesPivot]),
    payloadCreator(deleteIbanXsBanksCountriesPivot),
    {
        condition: (_, { getState }) => {
            const { openBanking } = getState() as RootState;

            return ![ApiState.Pending].includes(openBanking[MenuItem.IbanXSBanksCountriesPivot].tableLoadingState);
        }
    }
);
